import { createContext, useContext } from 'react'

export type IndexName = string & { _protect: '' }

/** Per-block display options, set in the block inspector and passed through the widget config. */
export type WidgetOptions = {
  /** Show the next four weeks as one list instead of a week-by-week switcher. */
  compactView: boolean
  /** Hide the large teaser image (and the series info tooltip) above the list. */
  hideImage: boolean
  /** Custom fallback image, used when neither celebration nor series has an image. */
  fallbackImageUrl: string
  fallbackImageAlt: string
  /** Custom text shown when no celebrations are found. */
  emptyText: string
}

export type WidgetConfig = {
  appId: string
  apiKey: string
  postsPerPage: number
  indexName: IndexName
  locationsPageUrl: string
  imgixSrcUrl: string
  imgixTargetUrl: string
  placeholderImageUrl: string
  siteUrl: string
  options: WidgetOptions
}

export const DEFAULT_WIDGET_OPTIONS: WidgetOptions = {
  compactView: false,
  hideImage: false,
  fallbackImageUrl: '',
  fallbackImageAlt: '',
  emptyText: ''
}

const ConfigContext = createContext<WidgetConfig>({
  appId: '',
  apiKey: '',
  postsPerPage: 10,
  indexName: 'services' as IndexName,
  locationsPageUrl: '',
  imgixSrcUrl: '',
  imgixTargetUrl: '',
  placeholderImageUrl: '',
  siteUrl: '',
  options: DEFAULT_WIDGET_OPTIONS
})

export function useConfigContext() {
  return useContext(ConfigContext)
}

/** Options with defaults applied, so older saved configs without `options` keep working. */
export function useWidgetOptions(): WidgetOptions {
  const { options } = useContext(ConfigContext)
  return { ...DEFAULT_WIDGET_OPTIONS, ...options }
}

export const ConfigProvider = ConfigContext.Provider
